// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// DRAFT AI-assistance notice (IP, License & Attribution Policy v1.3 §12d-1):
// This content was prepared with the assistance of AI tools and is, on this
// branch, a DRAFT pending human review. On the human-approved merge of its
// Merge Request this notice flips to the published variant (§12d-2).
// Bu icerik yapay zeka destekli araclarla hazirlanmis bir TASLAKTIR (§12d-1);
// insan onayiyla birlestirilmesinde yayimlanmis surumune (§12d-2) donusur.

/**
 * Bridge origin configuration.
 *
 * Adapted from the sibling AU0008 updater widget (AU0008 ADR-005 D4). Zoho
 * Creator embeds every widget as a non-addressable iframe, so requests are
 * broadcast to sibling/ancestor frames; origin discipline is the primary
 * client-side defence. The widget always posts with an EXPLICIT target origin
 * (never "*") and accepts a reply only when event.origin equals the known
 * Gateway origin for its environment (dev / stage / prod).
 *
 * The concrete per-tier origin strings are supplied by the build/runtime
 * environment at the composition root (main.tsx), never hardcoded here.
 */

/** The deployment tiers (Zoho Creator promotion: dev -> stage -> prod). */
export const BRIDGE_TIERS = ["dev", "stage", "prod"] as const;
export type BridgeTier = (typeof BRIDGE_TIERS)[number];

/** Resolved origin configuration for one running widget instance. */
export interface BridgeOriginConfig {
  readonly tier: BridgeTier;
  /** The known Widget API Gateway origin for this tier (absolute https origin). */
  readonly gatewayOrigin: string;
}

/** Narrow an unknown string to a BridgeTier, or null. */
export function asBridgeTier(value: string): BridgeTier | null {
  return (BRIDGE_TIERS as readonly string[]).includes(value)
    ? (value as BridgeTier)
    : null;
}

/**
 * Validate and build a BridgeOriginConfig. A non-absolute or empty origin is a
 * configuration fault surfaced early (ISO/IEC 5055 explicit handling), never a
 * silent fallback to "*".
 */
export function makeOriginConfig(
  tier: BridgeTier,
  gatewayOrigin: string,
): BridgeOriginConfig {
  if (!isAbsoluteOrigin(gatewayOrigin)) {
    throw new Error(
      `Invalid Gateway origin for tier "${tier}": an absolute https origin is required; never "*".`,
    );
  }
  return { tier, gatewayOrigin };
}

/**
 * An acceptable target/inbound origin is an absolute origin with scheme + host
 * and NO path/query/fragment, and never the wildcard "*". Strict so a
 * misconfiguration fails loudly rather than widening the trust boundary.
 */
export function isAbsoluteOrigin(value: string): boolean {
  if (value === "*" || value.length === 0) return false;
  try {
    const u = new URL(value);
    if (u.protocol !== "https:" && u.protocol !== "http:") return false;
    return value === u.origin;
  } catch {
    return false;
  }
}
